class QuantitySelector extends HTMLElement {
  constructor() {
    super();

    this.onQuantityDecreaseListener = this.decreaseQuantity.bind(this);
    this.onQuantityIncreaseListener = this.increaseQuantity.bind(this);
    this.onInputChangeListener = this.validateQuantity.bind(this);
  }

  selectors = {
    input: "input",
    button: {
      decrease: "[data-action='decrease-quantity']",
      increase: "[data-action='increase-quantity']"
    }
  };

  connectedCallback() {
    this.inputElement = this.querySelector(this.selectors.input);
    this.decreaseButtonElement = this.querySelector(this.selectors.button.decrease);
    this.increaseButtonElement = this.querySelector(this.selectors.button.increase);

    this.customDelegate(
      "click",
      this.selectors.button.decrease,
      this.onQuantityDecreaseListener
    );

    this.customDelegate(
      "click",
      this.selectors.button.increase,
      this.onQuantityIncreaseListener
    );

    this.inputElement.addEventListener("change", this.onInputChangeListener);

    this.validateQuantity();
  }

  decreaseQuantity() {
    this.inputElement.value = Math.max(parseInt(this.inputElement.value) - 1, 1);
    this.forceInputChange();
  }

  increaseQuantity() {
    this.inputElement.value = parseInt(this.inputElement.value) + 1;
    this.forceInputChange();
  }

  validateQuantity() {
    this.inputElement.value = Math.max(parseInt(this.inputElement.value) || 1, 1);

    this.decreaseButtonElement.disabled = parseInt(this.inputElement.value) - 1 < parseInt(this.inputElement.min);
    this.increaseButtonElement.disabled = parseInt(this.inputElement.value) + 1 > parseInt(this.inputElement.max);
  }

  forceInputChange() {
    this.inputElement.dispatchEvent(new Event("change"));
  }
}

if (!customElements.get("quantity-selector")) {
  customElements.define("quantity-selector", QuantitySelector);
}

class ProductFormActions extends HTMLElement {
  constructor() {
    super();

    this.onVariantChangedListener = this.onVariantChange.bind(this);
    this.onVariantAddedListener = this.onVariantAdd.bind(this);
  }

  selectors = {
    submit: "[data-add-to-cart]",
    submitText: "[data-add-to-cart-text]",
    message: "[data-product-form-message]"
  };

  attributes = {
    form: "form"
  };

  datasets = {
    minimumStockThreshold: "minimumStockThreshold"
  };

  modifiers = {
    adding: "adding",
    hidden: "hide",
    message: {
      error: "message--error",
      success: "message--success"
    }
  };

  messageTypes = {
    success: "success",
    error: "error"
  };

  connectedCallback() {
    this.productForm = document.forms[this.getAttribute(this.attributes.form)];

    this.submitButton = this.querySelector(this.selectors.submit);
    this.submitButtonText = this.querySelector(this.selectors.submitText);

    this.productFormMessage = this.querySelector(this.selectors.message);

    this.minimumStockThreshold = parseInt(this.dataset[this.datasets.minimumStockThreshold]);

    this.productForm?.addEventListener("variant:change", this.onVariantChangedListener);
    this.productForm?.addEventListener("variant:add", this.onVariantAddedListener);
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener("variant:change", this.onVariantChangedListener);
    this.productForm?.removeEventListener("variant:add", this.onVariantAddedListener);
  }

  onVariantChange(event) {
    let variant = event.detail.variant;

    if (!this.submitButton || !this.submitButtonText) {
      return;
    }

    if (!variant) {
      this.submitButton.disabled = true;
      this.submitButtonText.innerText = theme.strings.product.unavailable;

      return;
    }

    const inventoryPolicy = variant?.inventory_policy;
    const inventoryQuantity = parseInt(variant.inventory_quantity);

    if (inventoryPolicy === "continue") {
      this.submitButton.disabled = false;
      this.submitButtonText.innerText = theme.strings.product.add_to_cart;
    } else {
      if (!variant.available || (inventoryQuantity <= this.minimumStockThreshold)) {
        this.submitButton.disabled = true;
        this.submitButtonText.innerText = theme.strings.product.sold_out;
      } else {
        this.submitButton.disabled = false;
        this.submitButtonText.innerText = theme.strings.product.add_to_cart;
      }
    }
  }

  async onVariantAdd(event) {
    const { variant, formData } = event?.detail ?? {};

    if (!formData) {
      return;
    }

    this.updateProductFormMessage();

    const url = `${window.theme.routes.cart}/add.js`;

    this.submitButton.classList.add(this.modifiers.adding);

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          items: [{ ...formData }]
        }),
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const data = await response.json();

      const { status, description } = data;

      if (status === 422) {
        this.onVariantAddedError(description);
        return;
      }

      this.onVariantAddedSuccess(data, variant);
    } catch (error) {
      this.onVariantAddedError(error);
    }

    setTimeout(() => {
      this.submitButton.classList.remove(this.modifiers.adding);
    }, 1500);
  }

  disableButton() {
    if (!this.submitButton) {
      return;
    }

    this.submitButton.disabled = true;
  }

  enableButton() {
    if (!this.submitButton) {
      return;
    }

    this.submitButton.disabled = false;
  }

  forceSelection() {
    if (!this.submitButtonText) {
      return;
    }

    this.submitButtonText.innerText = theme.strings.product.complete_selection;
  }

  onVariantAddedSuccess(data, variant) {
    document.dispatchEvent(new CustomEvent("theme:cart:popdown", {
      detail: {
        variant
      }
    }));

    this.updateCart();
  }

  onVariantAddedError(error) {
    console.error(error);

    this.updateProductFormMessage(this.messageTypes.error, error);
  }

  updateProductFormMessage(type = null, message = "") {
    if (!this.productFormMessage) {
      return;
    }

    this.productFormMessage.innerHTML = message;

    switch (type) {
      case this.messageTypes.success: {
        this.productFormMessage.classList.remove(this.modifiers.message.error);
        this.productFormMessage.classList.add(this.modifiers.message.success);

        break;
      }

      case this.messageTypes.error: {
        this.productFormMessage.classList.remove(this.modifiers.message.success);
        this.productFormMessage.classList.add(this.modifiers.message.error);

        break;
      }

      default: {
        this.productFormMessage.classList.add(this.modifiers.hidden);
      }
    }

    this.productFormMessage.classList.toggle(this.modifiers.hidden, !!!message);
  }

  async updateCart() {
    const url = `${Shopify.routes.root}cart.js`;

    const response = await fetch(url).then(
      (data) => data.json()
    );

    document.dispatchEvent(new CustomEvent("theme:cart:change", {
      detail: {
        cart: response
      },
      bubbles: true
    }));
  }
}

if (!customElements.get("product-form-actions")) {
  customElements.define("product-form-actions", ProductFormActions);
}

class CompareAtPrice extends HTMLElement {
  constructor() {
    super();

    this.onVariantChangedListener = this.onVariantChange.bind(this);
  }

  modifiers = {
    hidden: "hide"
  };

  connectedCallback() {
    const formId = this.getAttribute("form");
    this.form = document.forms[formId];

    this.form?.addEventListener("variant:change", this.onVariantChangedListener);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("variant:change", this.onVariantChangedListener);
  }

  onVariantChange(event) {
    const variant = event.detail.variant;

    if (!variant) {
      return;
    }

    if (variant.compare_at_price > variant.price) {
      this.innerText = theme.formatMoney(variant.compare_at_price, theme.moneyFormat);
      this.classList.remove(this.modifiers.hidden);
    } else {
      this.innerText = "";
      this.classList.add(this.modifiers.hidden);
    }
  }
}

if (!customElements.get("compare-at-price")) {
  customElements.define("compare-at-price", CompareAtPrice);
}


class RegularPrice extends HTMLElement {
  constructor() {
    super();

    this.onVariantChangedListener = this.onVariantChange.bind(this);
  }

  modifiers = {
    sale: "price--sale"
  };

  connectedCallback() {
    const formId = this.getAttribute("form");
    this.form = document.forms[formId];

    this.form?.addEventListener("variant:change", this.onVariantChangedListener);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("variant:change", this.onVariantChangedListener);
  }

  onVariantChange(event) {
    const variant = event.detail.variant;

    if (!variant) {
      return;
    }

    this.innerText = theme.formatMoney(variant.price, theme.moneyFormat);

    if (variant.compare_at_price > variant.price) {
      this.classList.add(this.modifiers.sale);
    } else {
      this.classList.remove(this.modifiers.sale);
    }
  }
}

if (!customElements.get("regular-price")) {
  customElements.define("regular-price", RegularPrice);
}

class ProductInfo extends HTMLElement {
  constructor() {
    super();
  }

  selectors = {
    form: {
      container: "[data-product-form]",
      submit: "product-form-actions"
    },
    stitchedProductsJson: "[data-stitched-products-json]",
    additionalVariantsDataJson: "[data-additional-variants-data]"
  };

  datasets = {
    productHandle: "productHandle",
    enableHistoryState: "enableHistoryState",
    enableBackInStock: "enableBackInStock",
    enableProductStitching: "enableProductStitching",
    minimumStockThreshold: "minimumStockThreshold",
    option: "option",
    optionName: "optionName",
    index: "index"
  }

  modifiers = {
    hidden: "hide",
    disabled: "disabled"
  };

  colorOptionLabels = "color,colour,couleur,colore,farbe,색,色,fxärg,farve".split(",");
  sizeOptionLabels = "size,taille,größe,tamanho,tamaño,koko,サイズ".split(",");

  async connectedCallback() {
    this.product = await this.initProduct();

    if (!Object.keys(this.product).length) {
      return;
    }

    this.initElements();
    this.initProductForm();

    this.enableHistoryState = (this.dataset[this.datasets.enableHistoryState] === "true") || false;
    this.enableProductStitching = (this.dataset[this.datasets.enableProductStitching] === "true") || false;

    this.minimumStockThreshold = parseInt(this.dataset[this.datasets.minimumStockThreshold]);

    this.stitchedProducts = this.initStitchedProducts();
    this.additionalVariantsData = this.initAdditionalVariantsData();

    this.disableUnavailableOptions();
    this.disableUnavailableStitchedProducts();
  }

  initElements() {
    this.productFormElement = this.querySelector(this.selectors.form.container);
    this.productFormActions = this.querySelector(this.selectors.form.submit);
  }

  async initProduct() {
    const handle = this.dataset[this.datasets.productHandle];

    if (!handle) {
      return {};
    }

    const url = `/products/${handle}.js`;

    const response = await fetch(url).then(
      (data) => data.text()
    );

    return JSON.parse(response);
  }

  initStitchedProducts() {
    if (!this.enableProductStitching) {
      return {};
    }

    const stitchedProductsJson = this.querySelector(this.selectors.stitchedProductsJson);

    if (!stitchedProductsJson) {
      return {};
    }

    return JSON.parse(stitchedProductsJson.innerHTML);
  }

  initAdditionalVariantsData() {
    const additionalVariantsDataJson = this.querySelector(this.selectors.additionalVariantsDataJson);

    if (!additionalVariantsDataJson) {
      return {};
    }

    return JSON.parse(additionalVariantsDataJson.innerHTML);
  }

  initProductForm() {
    if (!this.productFormElement) {
      return null;
    }

    this.productForm = new Shopify.theme.productForm.ProductForm(
      this.productFormElement,
      this.product,
      {
        onOptionChange: this.onOptionChange.bind(this),
        onFormSubmit: this.onFormSubmit.bind(this),
        onQuantityChange: () => {},
        onPropertyChange: () => {}
      }
    );

    this.productOptionInputs = this.productForm.optionInputs.reduce(
      (options, input) => {
        const option = input.dataset[this.datasets.option];
        const inputs = options[option] || [];

        return {
          ...options,
          [option]: [...inputs, input]
        };
      },
      {}
    );

    this.currentVariant = this.productForm.variant();
  }

  onOptionChange(event) {
    this.selectVariant(event.dataset.variant);

    if (this.productFormActions) {
      this.productFormActions.updateProductFormMessage();
    }

    this.disableUnavailableOptions();
    this.disableUnavailableStitchedProducts();

    this.updateBrowserHistoryState();
  }

  onFormSubmit(event) {
    event.preventDefault();

    const areAllOptionsSelected = this.validateOptionSelection();

    if (!areAllOptionsSelected) {
      this.productFormActions.disableButton();
      this.productFormActions.forceSelection();

      return;
    }

    this.productFormActions.enableButton();

    const form = event.target;
    const formData = new FormData(form);
    const formDataJson = this.formDataToJSON(formData);

    this.productFormElement.dispatchEvent(new CustomEvent("variant:add", {
      bubbles: true,
      detail: {
        product: this.product,
        variant: this.currentVariant,
        formData: formDataJson
      }
    }));
  }

  selectVariant(variant) {
    const previousVariant = this.currentVariant;

    this.currentVariant = variant;

    if (this.additionalVariantsData.hasOwnProperty(this.currentVariant.id)) {
      this.currentVariant = {
        ...variant,
        ...this.additionalVariantsData[variant.id]
      };
    }

    this.productFormElement.dispatchEvent(new CustomEvent("variant:change", {
      bubbles: true,
      detail: {
        product: this.product,
        variant: this.currentVariant,
        previousVariant
      }
    }));
  }

  updateBrowserHistoryState() {
    if (!this.currentVariant || !this.enableHistoryState) {
      return;
    }

    const url = Shopify.theme.productForm.getUrlWithVariant(window.location.href, this.currentVariant.id);

    window.history.replaceState({ path: url }, "", url);
  }

  validateOptionSelection() {
    return Object.values(this.productOptionInputs).every(inputs => inputs.some(input => input.checked));
  }

  disableUnavailableOptions() {
    if (!Object.keys(this.productOptionInputs).length) {
      return;
    }

    const areAllOptionsSelected = this.validateOptionSelection();

    if (!areAllOptionsSelected) {
      return;
    }

    if (!this.currentVariant) {
      return;
    }

    const selectedOptions = this.getSelectedOptions(this.currentVariant);

    const productVariants = this.product.variants.map((variant) =>
      this.additionalVariantsData.hasOwnProperty(variant.id)
        ? {
          ...variant,
          ...this.additionalVariantsData[variant.id]
        }
        : variant
    );

    const availableOptions = this.getAvailableValues(
      productVariants,
      selectedOptions
    );

    Object.keys(this.productOptionInputs).forEach((option) => {
      const availableValues = availableOptions[option] ?? [];

      this.productOptionInputs[option].forEach((input) => {
        if (!availableValues.includes(input.value)) {
          input.classList.add(this.modifiers.disabled);
          return;
        }

        input.classList.remove(this.modifiers.disabled);
      });
    });
  }

  disableUnavailableStitchedProducts() {
    if (!Object.keys(this.productOptionInputs).length || !Object.keys(this.stitchedProducts).length) {
      return;
    }

    const areAllOptionsSelected = this.validateOptionSelection();

    if (!areAllOptionsSelected) {
      return;
    }

    Object.values(this.stitchedProducts).forEach((stitchedProduct) => {
      const stitchedProductId = stitchedProduct.id;

      const stitchedProductSwatch = this.querySelector(`[data-stitched-product-id='${stitchedProductId}']`);

      if (!stitchedProductSwatch) {
        return;
      }

      const stitchedProductVariant = stitchedProduct.current_variant;
      const stitchedProductOptions = stitchedProduct.options;

      const colorOption = stitchedProductOptions.find((option) => {
        const downcasedOptionName = option.name.toLowerCase();
        return this.colorOptionLabels.includes(downcasedOptionName);
      });

      if (!colorOption) {
        return false;
      }

      const colorOptionPosition = `option${colorOption.position}`;
      const colorOptionValue = stitchedProductVariant[colorOptionPosition];

      let selectedOptions = {
        ...this.getSelectedOptions(),
        [colorOptionPosition]: colorOptionValue
      };

      const stitchedProductAvailableOptions = this.getAvailableValues(stitchedProduct.variants, selectedOptions);

      stitchedProductOptions.forEach((stitchedProductOption) => {
        const optionPosition = `option${stitchedProductOption.position}`;

        if (optionPosition === colorOptionPosition) {
          return;
        }

        const downcasedOptionName = stitchedProductOption.name.toLowerCase();
        const isSizeOption = this.sizeOptionLabels.includes(downcasedOptionName);

        let availableValues = stitchedProductAvailableOptions[optionPosition] ?? [];
        let currentVariantOptionValue = this.currentVariant[optionPosition];

        if (!currentVariantOptionValue) {
          return;
        }

        if (isSizeOption) {
          availableValues = availableValues.map((availableValue) => availableValue.toLowerCase());
          currentVariantOptionValue = currentVariantOptionValue.toLowerCase();
        }

        if (!availableValues.includes(currentVariantOptionValue)) {
          stitchedProductSwatch.classList.add(this.modifiers.disabled);
          return;
        }

        stitchedProductSwatch.classList.remove(this.modifiers.disabled);
      });
    });
  }

  getSelectedOptions() {
    return this.productForm.options().reduce((options, option, index) => {
      return {
        ...options,
        [`option${index + 1}`]: option.value
      };
    }, {});
  }

  getAvailableValues(variants, selected, minimumStockThreshold = this.minimumStockThreshold) {
    let originalSelectedKeys = Object.keys(selected);
    let maxOptionKey = `option${originalSelectedKeys.length + 1}`;
    let variantOptions = variants.map((variant) => {
      let keys = Object.keys(variant).filter((key) => {
        return key.indexOf("option") === 0 || key === "available" || key === "inventory_quantity";
      });

      return Object.assign(
        {},
        {},
        ...keys.map((key) => {
          if (key === "available" || key === "inventory_quantity") {
            return {
              [key]: variant[key]
            };
          }

          if (key > maxOptionKey) {
            return {};
          }

          const value = variant[key];

          if (value) {
            return {
              [key]: value
            };
          }

          return {};
        })
      );
    });

    Object.keys(variantOptions[0]).forEach((key) => {
      if (key === "available" || key === "inventory_quantity") {
        return;
      }

      if (selected.hasOwnProperty(key)) {
        return;
      }

      selected[key] = null;
    });

    let selectedKeys = Object.keys(selected);
    let combinations =
      selectedKeys.length > 0
        ? selectedKeys.map((excludeKey) => {
          return Object.assign(
            {},
            ...selectedKeys.map((key) => {
              if (excludeKey === key) {
                return {};
              }

              return {
                [key]: selected[key]
              };
            })
          );
        })
        : [{}];

    let availableValues = {};
    variantOptions
      .map((options) => {
        return Object.assign(
          {},
          {},
          ...combinations.map((combination) => {
            const isSubset =
              Object.keys(combination)
                .map((key) => {
                  if (combination[key] === null) {
                    return true;
                  }
                  return (
                    options.hasOwnProperty(key) &&
                    combination[key] === options[key]
                  );
                })
                .includes(false) !== true;

            if (!isSubset) {
              return {};
            }

            if (!options.available) {
              return {};
            }

            if (options.inventory_quantity <= minimumStockThreshold) {
              return {};
            }

            return Object.assign(
              {},
              {},
              ...Object.keys(options)
                .filter((key) => {
                  return (
                    key !== "available" &&
                    key !== "inventory_quantity" &&
                    !combination.hasOwnProperty(key)
                  );
                })
                .map((optionName) => {
                  return {
                    [optionName]: [options[optionName]]
                  };
                })
            );
          })
        );
      })
      .filter((options) => {
        return Object.keys(options).length > 0;
      })
      .forEach((options) => {
        Object.keys(options).forEach((key) => {
          if (!availableValues.hasOwnProperty(key)) {
            availableValues[key] = [];
          }
          availableValues[key] = availableValues[key]
            .concat(options[key])
            .filter((value, index, self) => {
              return self.indexOf(value) === index;
            });
        });
      });

    return Object.assign(
      {},
      {},
      ...Object.keys(availableValues)
        .sort()
        .map((key) => {
          return {
            [key]: availableValues[key]
          };
        })
    );
  }

  formDataToJSON(formData) {
    return Array.from(formData.entries()).reduce((memo, [key, value]) => ({
      ...memo,
      [key]: value,
    }), {});
  }
}

if (!customElements.get("product-info")) {
  customElements.define("product-info", ProductInfo);
}

class ProductSlider extends HTMLElement {
  constructor() {
    super();

    this.swiperInstance = null;
  }

  selectors = {
    slide: "slider-slide",
    zoomMedia: "zoom-media"
  };

  modifiers = {
    initialized: "initialized"
  };

  options = {
    slidesPerView: "auto",
    spaceBetween: 0,
    loop: true,
    on: {
      afterInit: () => {
        this.classList.add(this.modifiers.initialized);
      },
      slideChange: this.onSlideChange.bind(this)
    },
    resistance: true,
    resistanceRatio: 0,
    navigation: {
      prevEl: "slider-button[type='prev']",
      nextEl: "slider-button[type='next']"
    },
    pagination: {
      el: "slider-pagination",
      bulletClass: "slider__pagination-bullet"
    }
  };

  get slides() {
    return [...this.querySelectorAll(this.selectors.slide)];
  }

  connectedCallback() {
    this.initSlider();
  }

  disconnectedCallback() {
    this.destroySlider();
  }

  initSlider() {
    if (this.swiperInstance) {
      return;
    }

    this.swiperInstance = new window.Swiper(this, this.options);
  }

  destroySlider(deleteInstance = true, clearStyles = true) {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.destroy(deleteInstance, clearStyles);
    this.swiperInstance = null;
  }

  onSlideChange() {
    const previousSlide = this.slides.find((slide) => {
      return parseInt(slide.dataset.index) === parseInt(this.swiperInstance.previousIndex)
    });

    const zoomMedia = previousSlide.querySelector(this.selectors.zoomMedia);

    if (zoomMedia) {
      zoomMedia.zoomOut();
    }
  }
}

if (!customElements.get("product-slider")) {
  customElements.define("product-slider", ProductSlider);
}
